import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, AlertTriangle, X, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { tutorApi, TUTOR_TOKEN_KEY, formatApiError } from "../../lib/tutorApi";

/**
 * V29 — Tutor self-delete card (rendered inside Tutor Account Settings).
 *
 * Shows a destructive Danger Zone block. Clicking "Delete My Profile" opens a
 * confirmation modal that requires typing "DELETE MY PROFILE" (defence in
 * depth against accidental taps). On success the local session is wiped, a
 * success toast is shown with the exact spec copy, and the user is redirected
 * to the tutor login page.
 */
export default function TutorSelfDeleteCard({ onDeleted }) {
    const [open, setOpen] = useState(false);
    const [phrase, setPhrase] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const phraseOk = phrase.trim() === "DELETE MY PROFILE";

    const handleDelete = async () => {
        setBusy(true);
        try {
            const res = await tutorApi.delete("/tutor/me", {
                data: { confirm_phrase: "DELETE MY PROFILE", reason: reason.trim() || undefined },
            });
            toast.success("Profile deleted", {
                description: res.data?.message ||
                    "Your profile has been deleted successfully. If you wish to rejoin Anand Tutorials in the future, please create a new tutor profile and complete the registration process.",
                duration: 8000,
            });
            // Wipe local session immediately
            localStorage.removeItem(TUTOR_TOKEN_KEY);
            try { onDeleted && onDeleted(); } catch { /* ignore */ }
            navigate("/tutor/login", { replace: true });
        } catch (err) {
            toast.error("Could not delete profile", {
                description: formatApiError(err.response?.data?.detail) || err.message,
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-5 mt-6" data-testid="tutor-self-delete-card">
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-red-100 border border-red-200">
                        <ShieldAlert className="w-5 h-5 text-red-700" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-base font-bold text-red-900">Danger Zone</h3>
                        <p className="text-xs text-red-800/80 mt-1 leading-relaxed">
                            Permanently delete your tutor profile, dashboard data, and access. This action <strong>cannot be undone</strong>.
                            To work with Anand Tutorials again you'll have to create a fresh tutor profile and complete the registration + approval flow.
                        </p>
                        <button
                            onClick={() => { setPhrase(""); setReason(""); setOpen(true); }}
                            className="inline-flex items-center gap-2 mt-3 px-3.5 py-2 text-xs font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-sm transition"
                            data-testid="tutor-self-delete-open-btn"
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Delete My Profile
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div className="fixed inset-0 z-[140] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150" data-testid="tutor-self-delete-modal">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white px-5 py-4 flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6" />
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider opacity-80">Permanent Action</div>
                                <div className="text-base font-bold">Delete My Profile</div>
                            </div>
                            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20" aria-label="Close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5 space-y-3 text-sm text-slate-700">
                            <p className="leading-relaxed">
                                Deleting your profile will permanently remove your account and dashboard access. To work
                                with Anand Tutorials again, you will need to create a new tutor profile and complete the
                                registration process from the beginning. <strong className="text-red-700">This action cannot be undone.</strong>
                            </p>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
                                    Type <code className="px-1 py-0.5 bg-slate-100 rounded">DELETE MY PROFILE</code> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={phrase}
                                    onChange={(e) => setPhrase(e.target.value)}
                                    placeholder="DELETE MY PROFILE"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
                                    data-testid="tutor-self-delete-phrase-input"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
                                    Reason (optional)
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={2}
                                    placeholder="Help us improve — tell us why (private, optional)"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30"
                                    data-testid="tutor-self-delete-reason-input"
                                />
                            </div>
                        </div>
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button
                                onClick={() => setOpen(false)}
                                disabled={busy}
                                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-50"
                                data-testid="tutor-self-delete-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={busy || !phraseOk}
                                className="px-3 py-1.5 text-sm font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                                data-testid="tutor-self-delete-confirm-btn"
                            >
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                Delete My Profile
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
